import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import useGroupData from "../useGroupData";

/**
 * Owns the fetch → enrich → init lifecycle for GROUPS mode ratings.
 *
 *   1. Fetch shared grading profile DTOs when sharedGradingProfileIds change
 *      (needed for gradingProfileStatus enrichment in fetchSelectedGroupInfo)
 *
 *   2. Fetch group ratings + comments when grade / template / groups are ready
 *      (or auto-set EPR when a homeroom group is selected without a profile)
 *
 *   3. Fetch all shared groups once on mount (populates allSharedGroupOptions,
 *      which feeds useGroupSelection's group options rebuild)
 *
 *   4. Re-fetch all shared groups when the location's school key changes
 *      (deep-link to a different school). Skips the initial mount — Effect 3
 *      already fetched. Subject changes do not retrigger this.
 *
 *   5. Clear selectedGroupContent when groups are deselected
 *
 *   6. Initialize comments once both fetches have resolved
 *
 * Selection / routing stays in GroupRatings and useGroupSelection:
 *   - Auto-set school location from router state
 *   - Restore preselected groups from router state (useGroupSelection)
 *   - Auto-select single grade
 *   - Ensure school template for SLE override
 *
 * @param {Object} params
 *
 * @returns {{
 *   sharedGradingProfileDtos: Array,
 *   fetchSelectedGroupInfo: Function,
 *   fetchSelectedGroupStudentComments: Function,
 *   fetchAllSharedGroups: Function,
 *   selectedGroupContent: Array,
 *   setSelectedGroupContent: Function
 * }}
 */
const useGroupRatingsLoader = ({
    // Selection state
    selectedGroups,
    selectedGrade,
    selectedTemplate,
    selectedEpr,
    gradingProfilesCurrentSem,
    gradingProfiles,
    sharedGradingProfileIds,
    // Context
    locationState,
    userDetails,
    schoolYearDto,
    currentTimePeriodDto,
    token,
    overrideTeacherGuid,
    // State setters for shared options (owned by GroupRatings, fed by fetchAllSharedGroups)
    setAllSharedGroupOptions,
    // State setter for shared profile DTOs (owned by GroupRatings)
    setSharedGradingProfileDtos,
    // Callbacks for init
    initRatings,
    initComments,
    // Dispatch for EPR auto-set
    dispatch
}) => {
    // Internal copy of sharedGradingProfileDtos — needed by useGroupData.fetchSelectedGroupInfo
    // for enrichment. Mirrors the value in GroupRatings state; updated together.
    const [sharedGradingProfileDtos, setLocalSharedGradingProfileDtos] = useState([]);
    const [selectedGroupContent, setSelectedGroupContent] = useState([]);
    const [existingStudentComments, setExistingStudentComments] = useState([]);

    const fetchedSharedGroupsRef = useRef(false);
    const isFirstLocationEffectRef = useRef(true);
    const locationSchoolKey = locationState?.selectedSchoolLocationKey;

    // ── useGroupData: fetch callbacks ──────────────────────────────────────────
    const { fetchSelectedGroupInfo, fetchSelectedGroupStudentComments, fetchAllSharedGroups } = useGroupData({
        gradingProfiles,
        schoolYearKey: schoolYearDto?.key,
        selectedGroups,
        selectedEpr,
        selectedGrade,
        selectedTemplate,
        setAllSharedGroupOptions,
        setExistingStudentComments,
        setSelectedGroupContent,
        initRatings,
        sharedGradingProfileDtos,
        timePeriod: currentTimePeriodDto?.key,
        token,
        userDetails,
        overrideTeacherGuid
    });

    // ── Effect 1: Fetch shared profile DTOs when profile IDs change ────────────
    // sharedGradingProfileDtos are needed by fetchSelectedGroupInfo to enrich
    // gradingProfileStatus on shared groups. Fetched once per unique set of IDs.
    useEffect(() => {
        if (!token) return;
        if (!sharedGradingProfileIds.length) {
            setLocalSharedGradingProfileDtos([]);
            setSharedGradingProfileDtos([]);
            return;
        }
        let alive = true;
        Promise.all(
            sharedGradingProfileIds.map((id) =>
                ElementaryProgressReportDao({ action: "getGradingProfileById", gradingProfileId: id, token })
            )
        )
            .then((responses) => {
                if (!alive) return;
                const dtos = responses.map((r) => r?.data?.payload).filter(Boolean);
                setLocalSharedGradingProfileDtos(dtos);
                setSharedGradingProfileDtos(dtos);
            })
            .catch((error) => {
                toast.error("There was a problem loading shared groups information. Please try again later.");
                console.error(error);
            });
        return () => {
            alive = false;
        };
    }, [sharedGradingProfileIds, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Effect 2: Fetch group ratings + comments when selection is ready ────────
    useEffect(() => {
        if (!selectedGrade || !selectedTemplate) return;
        if (!selectedGroups?.length) return;

        const hasHomeroomGroups = selectedGroups.some((g) => g.value?.startsWith("homeroom-"));
        if (hasHomeroomGroups && !selectedEpr) {
            const eprForGrade = gradingProfilesCurrentSem?.find((p) => p.gradeName === selectedGrade);
            if (eprForGrade) dispatch({ type: "SET_SELECTED_EPR", payload: eprForGrade });
            return;
        }

        fetchSelectedGroupInfo();
        fetchSelectedGroupStudentComments();
    }, [
        selectedEpr,
        selectedGrade,
        selectedTemplate,
        selectedGroups,
        fetchSelectedGroupInfo,
        fetchSelectedGroupStudentComments,
        gradingProfilesCurrentSem,
        dispatch
    ]);

    // ── Effect 3: Fetch all shared groups once on mount ────────────────────────
    useEffect(() => {
        if (fetchedSharedGroupsRef.current) return;
        if (!userDetails?.uid || !token || !schoolYearDto?.key || !currentTimePeriodDto?.key) return;
        fetchedSharedGroupsRef.current = true;
        fetchAllSharedGroups();
    }, [fetchAllSharedGroups, userDetails, token, schoolYearDto, currentTimePeriodDto]);

    // ── Effect 4: Re-fetch shared groups when school context changes ────────────
    // Skip the first run — Effect 3 already fetched on mount. Subject changes
    // rewrite locationState (subjectId) but must not refetch all shared groups.
    useEffect(() => {
        if (isFirstLocationEffectRef.current) {
            isFirstLocationEffectRef.current = false;
            return;
        }
        if (!fetchedSharedGroupsRef.current) return;
        fetchAllSharedGroups();
    }, [locationSchoolKey, fetchAllSharedGroups]);

    // ── Effect 5: Clear group content when all groups are deselected ───────────
    useEffect(() => {
        if (selectedGroups.length === 0 && selectedGroupContent.length !== 0) {
            setSelectedGroupContent([]);
        }
    }, [selectedGroups, selectedGroupContent]);

    // ── Effect 6: Initialize comments once both fetches have resolved ───────────
    // fetchSelectedGroupInfo and fetchSelectedGroupStudentComments are called in
    // parallel. selectedGroupContent may not be set when comments arrive.
    // This effect fires when both are available so the groupId-prefixed key is
    // built correctly for each comment.
    //
    // Only re-init when the comments payload changes (or on first hydration).
    // A ratings-only save refreshes selectedGroupContent; re-running init then
    // would overwrite in-progress fluency/comment edits with stale server WPM.
    const hydratedCommentsRef = useRef(null);
    const hydratedContentRef = useRef(null);

    useEffect(() => {
        if (!selectedGroupContent.length) {
            hydratedCommentsRef.current = null;
            hydratedContentRef.current = null;
            return;
        }
        if (!existingStudentComments.length) return;

        const commentsChanged = hydratedCommentsRef.current !== existingStudentComments;
        const needsInitialHydration = hydratedCommentsRef.current == null || hydratedContentRef.current == null;

        hydratedCommentsRef.current = existingStudentComments;
        hydratedContentRef.current = selectedGroupContent;

        if (!needsInitialHydration && !commentsChanged) return;

        initComments(existingStudentComments, selectedGroupContent);
    }, [existingStudentComments, selectedGroupContent, initComments]);

    return {
        fetchSelectedGroupInfo,
        fetchSelectedGroupStudentComments,
        fetchAllSharedGroups,
        selectedGroupContent,
        setSelectedGroupContent
    };
};

export default useGroupRatingsLoader;
